import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Shield, Check, ChevronDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayName, formatPronouns } from '@/lib/displayName';
import { badgeStyle } from '@/lib/badgeStyle';

const NAVY = '#1D2D44';
const GOLD = '#C9A84C';
const INK = '#0F1A2A';
const PAPER = '#16243D';
const MUTED = '#8FA0BD';
const LINE = 'rgba(201,168,76,0.22)';
const TEXT = '#F5F1E6';
const SAGE = '#5E7F63';

const Picker = ({ value, onChange, members, exclude, label, testid }) => (
  <div className="flex-1 min-w-0">
    <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>{label}</p>
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testid}
        className="w-full appearance-none rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2"
        style={{ backgroundColor: PAPER, color: TEXT, border: `1px solid ${LINE}` }}
      >
        <option value="">— Select a member —</option>
        {members
          .filter((m) => m.user_id !== exclude)
          .map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {formatDisplayName(m.first_name || m.name, m.last_name_initial)} · {m.email}
              {m.map_status === 'complete' ? ' · ✓' : ''}
            </option>
          ))}
      </select>
      <ChevronDown
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: GOLD }} size={16}
      />
    </div>
  </div>
);

const CompareRow = ({ label, sub, valA, valB, match, testid, italic }) => (
  <div
    data-testid={testid}
    className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] gap-3 md:gap-5 py-4"
    style={{ borderTop: `1px solid rgba(201,168,76,0.10)` }}
  >
    <div className="md:pr-2">
      <p className="font-serif text-base" style={{ color: TEXT }}>{label}</p>
      {sub && <p className="text-xs mt-1" style={{ color: MUTED }}>{sub}</p>}
      {match && (
        <span
          className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={{ backgroundColor: 'rgba(94,127,99,0.25)', color: '#A3D9A5' }}
        >
          <Check size={10} /> Match
        </span>
      )}
    </div>
    <div
      className="rounded-xl p-4 text-sm whitespace-pre-wrap"
      style={{
        backgroundColor: PAPER,
        border: `1px solid ${match ? 'rgba(94,127,99,0.45)' : LINE}`,
        color: TEXT,
        fontStyle: italic ? 'italic' : 'normal',
        fontFamily: italic ? 'Playfair Display, serif' : undefined,
        minHeight: '48px',
      }}
    >
      {valA || <span style={{ color: MUTED }}>— No answer —</span>}
    </div>
    <div
      className="rounded-xl p-4 text-sm whitespace-pre-wrap"
      style={{
        backgroundColor: PAPER,
        border: `1px solid ${match ? 'rgba(94,127,99,0.45)' : LINE}`,
        color: TEXT,
        fontStyle: italic ? 'italic' : 'normal',
        fontFamily: italic ? 'Playfair Display, serif' : undefined,
        minHeight: '48px',
      }}
    >
      {valB || <span style={{ color: MUTED }}>— No answer —</span>}
    </div>
  </div>
);

const SectionHeader = ({ kicker, title }) => (
  <div className="mt-10 mb-2">
    <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: GOLD }}>{kicker}</p>
    <h2 className="font-serif text-2xl" style={{ color: TEXT }}>{title}</h2>
  </div>
);

const ComparePage = () => {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [aId, setAId] = useState(searchParams.get('a') || null);
  const [bId, setBId] = useState(searchParams.get('b') || null);
  const [data, setData] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const api = axiosAuth();
        const r = await api.get('/admin/members');
        setMembers(r.data);
      } catch (e) {
        if (e.response?.status === 403) {
          toast.error('Not authorized.');
          navigate('/dashboard');
        } else {
          toast.error('Failed to load members.');
        }
      } finally {
        setLoadingMembers(false);
      }
    })();
  }, [axiosAuth, navigate]);

  const runCompare = useCallback(async () => {
    if (!aId || !bId) {
      setData(null);
      return;
    }
    if (aId === bId) {
      toast.info('Pick two different members.');
      return;
    }
    setLoadingCompare(true);
    try {
      const api = axiosAuth();
      const r = await api.get('/admin/compare', { params: { user_a: aId, user_b: bId } });
      setData(r.data);
      setSearchParams({ a: aId, b: bId });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Could not load comparison.');
    } finally {
      setLoadingCompare(false);
    }
  }, [aId, bId, axiosAuth, setSearchParams]);

  useEffect(() => {
    if (aId && bId && aId !== bId) {
      runCompare();
    } else {
      setData(null);
    }
  }, [aId, bId, runCompare]);

  const a = data?.a;
  const b = data?.b;

  const rhythmRows = useMemo(() => {
    if (!data) return [];
    const labels = {
      communication_style: 'Communication style',
      affection_style: 'Affection style',
      conflict_style: 'Conflict style',
      social_energy: 'Social energy',
      pace_preference: 'Pace preference',
      love_language_awareness: 'Love language awareness',
    };
    // Pretty-print the love_language_awareness enum for display.
    const prettyLL = (v) => {
      if (v === 'known') return 'Known';
      if (v === 'exploring') return 'Exploring';
      return v;
    };
    return Object.entries(labels).map(([key, label]) => {
      const r = data.rhythm_alignment[key] || {};
      if (key === 'love_language_awareness') {
        return {
          key, label,
          valA: prettyLL(r.a), valB: prettyLL(r.b),
          match: !!r.match,
        };
      }
      return { key, label, valA: r.a, valB: r.b, match: !!r.match };
    });
  }, [data]);

  const llAwarenessNote = useMemo(() => {
    if (!data) return null;
    const llA = data.rhythm_alignment?.love_language_awareness?.a;
    const llB = data.rhythm_alignment?.love_language_awareness?.b;
    if (llA === 'exploring' || llB === 'exploring') {
      return 'Self-awareness developing — compatibility based on values and rhythm instead';
    }
    return null;
  }, [data]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      <header
        className="sticky top-0 z-40 backdrop-blur"
        style={{ backgroundColor: 'rgba(15,26,42,0.9)', borderBottom: `1px solid ${LINE}` }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center text-sm" style={{ color: MUTED }}
            data-testid="compare-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Back to Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={16} style={{ color: GOLD }} />
            <span className="font-serif text-lg" style={{ color: TEXT }}>
              Compatibility View · Our Love <Heart className="inline-block w-4 h-4 mx-1" style={{ color: GOLD }} fill={GOLD} strokeWidth={1.5} /> Connection
            </span>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="compare-page">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.3em] mb-1" style={{ color: GOLD }}>
            Side-by-Side Comparison
          </p>
          <h1 className="font-serif text-4xl" style={{ color: TEXT }}>Compare Members</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: MUTED }}>
            Pick two members to view their Relationship Map answers, rhythm, and profile side-by-side.
            Matching rhythm fields are highlighted to help you assess alignment.
          </p>
        </div>

        {/* Pickers */}
        <div
          className="rounded-2xl p-5 mb-8"
          style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
        >
          {loadingMembers ? (
            <p style={{ color: MUTED }}>Loading members…</p>
          ) : (
            <div className="flex flex-col md:flex-row gap-4 md:items-end">
              <Picker
                value={aId}
                onChange={setAId}
                members={members}
                exclude={bId}
                label="Member A"
                testid="picker-a"
              />
              <div className="hidden md:flex items-center justify-center w-16 pb-2" style={{ color: GOLD }}>
                <Sparkles size={18} />
              </div>
              <Picker
                value={bId}
                onChange={setBId}
                members={members}
                exclude={aId}
                label="Member B"
                testid="picker-b"
              />
              <Button
                onClick={() => { setAId(null); setBId(null); setData(null); setSearchParams({}); }}
                variant="outline"
                data-testid="clear-compare-btn"
                className="rounded-full md:ml-2"
                style={{ border: `1px solid ${LINE}`, backgroundColor: 'transparent', color: TEXT }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {loadingCompare && (
          <div className="py-12 text-center" style={{ color: MUTED }}>Loading comparison…</div>
        )}

        {!loadingCompare && !data && (aId && bId) && (
          <div className="py-12 text-center" style={{ color: MUTED }}>—</div>
        )}

        {!loadingCompare && !data && !(aId && bId) && (
          <div
            className="rounded-2xl p-10 text-center"
            style={{ backgroundColor: PAPER, border: `1px dashed ${LINE}`, color: MUTED }}
            data-testid="compare-empty-state"
          >
            <Sparkles size={20} className="mx-auto mb-3" style={{ color: GOLD }} />
            <p>Select two members above to begin a comparison.</p>
          </div>
        )}

        {data && a && b && (
          <div data-testid="compare-results">
            {/* Identity Header */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] gap-3 md:gap-5 mb-4">
              <div />
              <div className="rounded-2xl p-5" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }} data-testid="card-a">
                <p className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Member A</p>
                <h3 className="font-serif text-2xl mt-1" style={{ color: TEXT }}>
                  {formatDisplayName(a.first_name || a.name, a.last_name_initial)}
                </h3>
                {formatPronouns(a.pronouns) && (
                  <p className="text-xs mt-0.5" style={{ color: MUTED }} data-testid="card-a-pronouns">
                    {formatPronouns(a.pronouns)}
                  </p>
                )}
                <p className="text-sm" style={{ color: MUTED }}>{a.email}</p>
                <div className="flex gap-4 mt-3 text-sm" style={{ color: TEXT }}>
                  <span><span style={{ color: MUTED }}>Age:</span> {a.age ?? '—'}</span>
                  <span><span style={{ color: MUTED }}>City:</span> {a.city || '—'}</span>
                </div>
                {a.is_founding_member && (
                  <span className="inline-block mt-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
                    style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}>
                    Founding · Paid
                  </span>
                )}
              </div>
              <div className="rounded-2xl p-5" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }} data-testid="card-b">
                <p className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Member B</p>
                <h3 className="font-serif text-2xl mt-1" style={{ color: TEXT }}>
                  {formatDisplayName(b.first_name || b.name, b.last_name_initial)}
                </h3>
                {formatPronouns(b.pronouns) && (
                  <p className="text-xs mt-0.5" style={{ color: MUTED }} data-testid="card-b-pronouns">
                    {formatPronouns(b.pronouns)}
                  </p>
                )}
                <p className="text-sm" style={{ color: MUTED }}>{b.email}</p>
                <div className="flex gap-4 mt-3 text-sm" style={{ color: TEXT }}>
                  <span><span style={{ color: MUTED }}>Age:</span> {b.age ?? '—'}</span>
                  <span><span style={{ color: MUTED }}>City:</span> {b.city || '—'}</span>
                </div>
                {b.is_founding_member && (
                  <span className="inline-block mt-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
                    style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}>
                    Founding · Paid
                  </span>
                )}
              </div>
            </div>

            {/* Quick Alignment Summary */}
            <div
              className="rounded-2xl p-5 mb-4 flex flex-wrap items-center gap-5"
              style={{ backgroundColor: 'rgba(94,127,99,0.10)', border: `1px solid rgba(94,127,99,0.30)` }}
              data-testid="alignment-summary"
            >
              <div>
                <p className="text-[10px] uppercase tracking-widest" style={{ color: SAGE }}>Rhythm matches</p>
                <p className="font-serif text-2xl" style={{ color: TEXT }}>
                  {rhythmRows.filter((r) => r.match).length}/{rhythmRows.length}
                </p>
              </div>
              <div className="h-10 w-px" style={{ backgroundColor: LINE }} />
              <div>
                <p className="text-[10px] uppercase tracking-widest" style={{ color: SAGE }}>Shared badges</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {data.shared_badges.length === 0 ? (
                    <p style={{ color: MUTED }}>None yet</p>
                  ) : (
                    data.shared_badges.map((b) => (
                      <span key={b} className="text-xs px-2 py-0.5 rounded-full" style={badgeStyle(b, true)}>
                        {b}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Relationship Map Reflections */}
            <SectionHeader kicker="The Relationship Map" title="Reflections by Checkpoint" />
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(15,26,42,0.6)', border: `1px solid ${LINE}` }}>
              {a.reflections.map((rA, i) => {
                const rB = b.reflections[i] || {};
                return (
                  <CompareRow
                    key={rA.checkpoint_id}
                    testid={`compare-checkpoint-${rA.checkpoint_id}`}
                    label={`${rA.order}. ${rA.checkpoint_name}`}
                    sub={rA.checkpoint_subtitle}
                    valA={rA.serious_answer}
                    valB={rB.serious_answer}
                    italic={false}
                  />
                );
              })}
            </div>

            {/* Relationship Rhythm */}
            <SectionHeader kicker="Profile · Rhythm" title="Relationship Rhythm" />
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(15,26,42,0.6)', border: `1px solid ${LINE}` }}>
              {rhythmRows.map((row) => (
                <CompareRow
                  key={row.key}
                  testid={`compare-rhythm-${row.key}`}
                  label={row.label}
                  valA={row.valA}
                  valB={row.valB}
                  match={row.match}
                />
              ))}
              {llAwarenessNote && (
                <p
                  data-testid="ll-awareness-note"
                  className="mt-4 text-sm italic"
                  style={{ color: MUTED }}
                >
                  {llAwarenessNote}
                </p>
              )}
              {(a.ll_suggested_pattern || b.ll_suggested_pattern) && (
                <div
                  data-testid="ll-suggested-patterns"
                  className="mt-4 rounded-xl p-4"
                  style={{
                    backgroundColor: 'rgba(94,127,99,0.10)',
                    border: '1px solid rgba(94,127,99,0.30)',
                  }}
                >
                  <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: SAGE }}>
                    Possible pattern (optional insight)
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm" style={{ color: TEXT }}>
                    <div>
                      <span style={{ color: MUTED }}>Member A:</span>{' '}
                      {a.ll_suggested_pattern || <span style={{ color: MUTED }}>—</span>}
                    </div>
                    <div>
                      <span style={{ color: MUTED }}>Member B:</span>{' '}
                      {b.ll_suggested_pattern || <span style={{ color: MUTED }}>—</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* My Energy + How I Love */}
            <SectionHeader kicker="Profile · Voice" title="My Energy & How I Love" />
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(15,26,42,0.6)', border: `1px solid ${LINE}` }}>
              <CompareRow
                testid="compare-my-energy"
                label="My Energy"
                sub="the vibe they bring"
                valA={a.my_energy}
                valB={b.my_energy}
                italic
              />
              <CompareRow
                testid="compare-how-i-love"
                label="How I Love"
                sub="the way they give love"
                valA={a.how_i_love}
                valB={b.how_i_love}
                italic
              />
            </div>

            {/* Intentionality Badges */}
            <SectionHeader kicker="Profile · Intent" title="Intentionality Badges" />
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] gap-3 md:gap-5 py-4">
              <div className="md:pr-2">
                <p className="font-serif text-base" style={{ color: TEXT }}>Badges</p>
                <p className="text-xs mt-1" style={{ color: MUTED }}>Shared badges highlighted in gold.</p>
              </div>
              {[a, b].map((side, idx) => (
                <div
                  key={idx}
                  className="rounded-xl p-4 flex flex-wrap gap-2 min-h-[48px]"
                  style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
                  data-testid={`badges-${idx === 0 ? 'a' : 'b'}`}
                >
                  {side.intentionality_badges.length === 0 ? (
                    <span className="text-sm" style={{ color: MUTED }}>— None —</span>
                  ) : (
                    side.intentionality_badges.map((bg) => {
                      const shared = data.shared_badges.includes(bg);
                      return (
                        <span
                          key={bg}
                          className="text-xs px-3 py-1 rounded-full"
                          style={badgeStyle(bg, shared)}
                        >
                          {bg}
                        </span>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            {/* Guided Prompts */}
            <SectionHeader kicker="Profile · Voice" title="Guided Prompts" />
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(15,26,42,0.6)', border: `1px solid ${LINE}` }}>
              {(() => {
                const promptsA = a.guided_prompts || [];
                const promptsB = b.guided_prompts || [];
                const allPrompts = Array.from(
                  new Set([...promptsA.map((p) => p.prompt), ...promptsB.map((p) => p.prompt)])
                );
                if (allPrompts.length === 0) {
                  return (
                    <p className="text-sm" style={{ color: MUTED }}>
                      Neither member has answered any guided prompts yet.
                    </p>
                  );
                }
                return allPrompts.map((promptText) => {
                  const ansA = promptsA.find((p) => p.prompt === promptText)?.answer;
                  const ansB = promptsB.find((p) => p.prompt === promptText)?.answer;
                  const slug = promptText.slice(0, 40).replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
                  return (
                    <CompareRow
                      key={promptText}
                      testid={`compare-prompt-${slug}`}
                      label={promptText}
                      valA={ansA}
                      valB={ansB}
                      italic
                    />
                  );
                });
              })()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ComparePage;
